import { HttpErrorResponse } from '@angular/common/http';

/** Shown when the server returns an HTML error page (e.g. nginx 502) instead of JSON. */
export const UNABLE_TO_GET_RESPONSE_MESSAGE =
  'Unable to get the response. Please try again after some time.';

export function isHtmlErrorResponse(body: unknown): boolean {
  if (typeof body !== 'string') {
    return false;
  }
  const sample = body.trim().slice(0, 1200).toLowerCase();
  if (!sample) {
    return false;
  }
  return (
    sample.startsWith('<!doctype html') ||
    sample.startsWith('<html') ||
    sample.includes('<html') ||
    /<head[\s>]/i.test(sample) ||
    /<body[\s>]/i.test(sample) ||
    /<center>/i.test(sample) ||
    /<title>/.test(sample) ||
    sample.includes('</html>')
  );
}

/** Generic proxy / nginx / browser HTTP text — not a useful API message. */
export function isInfrastructureErrorMessage(message: string): boolean {
  if (!message?.trim()) {
    return false;
  }
  if (isHtmlErrorResponse(message)) {
    return true;
  }
  const m = message.toLowerCase().trim();
  if (m.length > 280) {
    return true;
  }
  const markers = [
    'bad gateway',
    'internal server error',
    'gateway timeout',
    'service unavailable',
    'http failure response',
    'unexpected token <',
    'unexpected token \'<\'',
    'nginx',
    'syntaxerror',
    'error occurred while trying to proxy',
  ];
  return markers.some((part) => m.includes(part));
}

/** Safe text for toast / alert (never show raw HTML or nginx pages). */
export function sanitizeDisplayMessage(
  text: string | null | undefined,
  fallback = UNABLE_TO_GET_RESPONSE_MESSAGE
): string {
  if (text == null || String(text).trim() === '') {
    return fallback;
  }
  const t = String(text).trim();
  if (isHtmlErrorResponse(t) || isInfrastructureErrorMessage(t)) {
    return UNABLE_TO_GET_RESPONSE_MESSAGE;
  }
  return t;
}

function messageFromErrorBody(body: Record<string, unknown>): string | null {
  const directKeys = [
    'message',
    'Message',
    'error',
    'Error',
    'data',
    'Data',
    'detail',
    'Detail',
    'msg',
    'description',
    'statusMessage',
  ];
  for (const key of directKeys) {
    const val = body[key];
    if (typeof val === 'string' && val.trim()) {
      const trimmed = val.trim();
      if (isHtmlErrorResponse(trimmed) || isInfrastructureErrorMessage(trimmed)) {
        continue;
      }
      return trimmed;
    }
    if (Array.isArray(val)) {
      const messages = val
        .filter((entry): entry is string => typeof entry === 'string' && !!entry.trim())
        .map((entry) => entry.trim())
        .filter((entry) => !isHtmlErrorResponse(entry) && !isInfrastructureErrorMessage(entry));
      if (messages.length > 0) {
        return messages.join(', ');
      }
    }
  }

  const nested = body['error'];
  if (nested && typeof nested === 'object') {
    const nestedMessage = messageFromErrorBody(nested as Record<string, unknown>);
    if (nestedMessage) {
      return nestedMessage;
    }
  }

  const data = body['data'];
  if (typeof data === 'string' && data.trim()) {
    const trimmed = data.trim();
    if (!isHtmlErrorResponse(trimmed) && !isInfrastructureErrorMessage(trimmed)) {
      return trimmed;
    }
  }
  if (data && typeof data === 'object') {
    const dataMessage = messageFromErrorBody(data as Record<string, unknown>);
    if (dataMessage) {
      return dataMessage;
    }
  }

  const errors = body['errors'];
  if (errors && typeof errors === 'object') {
    const values = Object.values(errors as Record<string, unknown>);
    for (const entry of values) {
      if (Array.isArray(entry) && typeof entry[0] === 'string' && entry[0].trim()) {
        const msg = entry[0].trim();
        if (!isInfrastructureErrorMessage(msg) && !isHtmlErrorResponse(msg)) {
          return msg;
        }
      }
      if (typeof entry === 'string' && entry.trim()) {
        const msg = entry.trim();
        if (!isInfrastructureErrorMessage(msg) && !isHtmlErrorResponse(msg)) {
          return msg;
        }
      }
    }
  }

  return null;
}

function errorBodyLooksLikeHtml(error: HttpErrorResponse): boolean {
  if (isHtmlErrorResponse(error.error)) {
    return true;
  }
  if (error.error && typeof error.error === 'object') {
    for (const val of Object.values(error.error as Record<string, unknown>)) {
      if (typeof val === 'string' && isHtmlErrorResponse(val)) {
        return true;
      }
    }
  }
  return false;
}

/** Parsed API error text only; null if body is empty, HTML, or not a known JSON shape. */
export function extractApiErrorMessage(error: HttpErrorResponse): string | null {
  const body = error.error;

  if (body == null || body === '') {
    return null;
  }

  if (body instanceof Blob || body instanceof ProgressEvent) {
    return null;
  }

  if (isHtmlErrorResponse(body)) {
    return null;
  }

  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (!trimmed || isHtmlErrorResponse(trimmed)) {
      return null;
    }
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed) as Record<string, unknown>;
        return messageFromErrorBody(parsed);
      } catch {
        return null;
      }
    }
    if (isInfrastructureErrorMessage(trimmed) || trimmed.length > 280) {
      return null;
    }
    return trimmed;
  }

  if (typeof body === 'object') {
    return messageFromErrorBody(body as Record<string, unknown>);
  }

  return null;
}

/** Best-effort message from API error body; uses fallback when nothing usable is found. */
export function extractHttpErrorMessage(
  error: HttpErrorResponse,
  fallback: string
): string {
  return extractApiErrorMessage(error) ?? fallback;
}

/** Default user-facing text when the API body has no usable message. */
export function defaultHttpErrorMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Bad Request';
    case 401:
      return 'Login to continue';
    case 403:
      return 'Forbidden';
    case 404:
      return 'Not Found';
    case 413:
      return 'Large File Size';
    case 422:
      return 'Server was unable to process the request';
    case 429:
      return 'Too many Request. Try After some time';
    case 504:
      return 'Session expired';
    default:
      return UNABLE_TO_GET_RESPONSE_MESSAGE;
  }
}

function statusTextFallback(error: HttpErrorResponse, status: number): string {
  const text = error.statusText?.trim();
  if (text && text.toLowerCase() !== 'ok') {
    return text;
  }
  return defaultHttpErrorMessage(status);
}

function shouldUseUnableForStatus(status: number): boolean {
  if (status === 0 || status === 502 || status === 503) {
    return true;
  }
  if (status >= 500 && status < 600 && status !== 504) {
    return true;
  }
  return false;
}

/** API error text when present; otherwise a safe status / gateway message (never raw HTML). */
export function resolveHttpErrorMessage(error: HttpErrorResponse): string {
  const status = error.status || 0;

  if (errorBodyLooksLikeHtml(error)) {
    return UNABLE_TO_GET_RESPONSE_MESSAGE;
  }

  const apiMessage = extractApiErrorMessage(error);
  if (apiMessage) {
    const fallback =
      status === 400 ? defaultHttpErrorMessage(400) : UNABLE_TO_GET_RESPONSE_MESSAGE;
    return sanitizeDisplayMessage(apiMessage, fallback);
  }

  if (status === 400) {
    return statusTextFallback(error, 400);
  }

  if (isInfrastructureErrorMessage(error.message || '')) {
    return UNABLE_TO_GET_RESPONSE_MESSAGE;
  }

  if (status === 504) {
    return 'Session expired';
  }

  if (shouldUseUnableForStatus(status)) {
    return UNABLE_TO_GET_RESPONSE_MESSAGE;
  }

  return statusTextFallback(error, status);
}
